import type { RecipeCardData } from '../components/RecipeCard';
import { Ingredient, splitInstructions } from './recipeFormat';

// Recipe cards for recipes that don't have one stored -- imported, manual,
// and older recipes, whose method is just instruction text. No AI: each
// ingredient goes on the first step that mentions it, and steps chain one
// into the next (so a two-bowl recipe shows as a single line of steps
// rather than two branches -- simpler than an AI-built card, but never
// wrong about what's in the recipe).
//
// All-or-nothing, like the backend's build_card: if any ingredient can't be
// found in the steps, there's no card at all rather than a partial one.

// Common cooking verbs, used to pick each step's action label out of its
// sentence ("In a large bowl, whisk the eggs" -> "whisk"). Deliberately
// leaves out words that are just as often nouns (oil, salt, butter, top).
const VERBS = new Set([
  'add', 'arrange', 'assemble', 'bake', 'baste', 'beat', 'blanch', 'blend', 'boil', 'braise', 'bring',
  'broil', 'brown', 'brush', 'caramelize', 'char', 'chill', 'chop', 'coat', 'combine', 'cook', 'cool',
  'cover', 'crack', 'cream', 'crumble', 'crush', 'cube', 'cut', 'deglaze', 'dice', 'dip', 'dissolve',
  'divide', 'drain', 'dredge', 'drizzle', 'dust', 'flip', 'fold', 'form', 'freeze', 'fry', 'garnish',
  'grate', 'grease', 'grill', 'grind', 'halve', 'heat', 'knead', 'layer', 'line', 'marinate', 'mash',
  'measure', 'melt', 'microwave', 'mince', 'mix', 'peel', 'place', 'poach', 'pound', 'pour', 'preheat',
  'press', 'process', 'pulse', 'puree', 'purée', 'reduce', 'refrigerate', 'reheat', 'rest', 'rinse',
  'roast', 'roll', 'rub', 'saute', 'sauté', 'scatter', 'scoop', 'scramble', 'sear', 'season', 'serve',
  'shake', 'shape', 'shred', 'sift', 'simmer', 'skewer', 'slice', 'smash', 'soak', 'spoon', 'spread',
  'sprinkle', 'squeeze', 'steam', 'stir', 'strain', 'stuff', 'swirl', 'thicken', 'toast', 'toss',
  'transfer', 'trim', 'warm', 'wash', 'whip', 'whisk', 'wrap', 'zest',
]);

// "Stir in the sugar" reads better on a card as "stir in" than "stir".
const PARTICLES = new Set(['in', 'together']);

// Words that describe an ingredient rather than name it ("2 large eggs",
// "unsalted butter, softened"), dropped when matching against step text.
const DESCRIPTORS = new Set([
  'large', 'medium', 'small', 'fresh', 'freshly', 'chopped', 'diced', 'minced', 'sliced', 'grated',
  'shredded', 'ground', 'whole', 'boneless', 'skinless', 'unsalted', 'salted', 'softened', 'melted',
  'room', 'temperature', 'cold', 'warm', 'finely', 'roughly', 'thinly', 'divided', 'optional', 'packed',
  'extra', 'virgin', 'low', 'sodium', 'brewed', 'strong', 'ripe', 'peeled', 'cooked', 'uncooked', 'dried',
  'frozen', 'canned', 'raw', 'plain',
]);

const words = (text: string): string[] => text.toLowerCase().match(/[a-zà-ÿ]+(?:-[a-zà-ÿ]+)*/g) || [];

const singular = (w: string) =>
  w.endsWith('ies') ? `${w.slice(0, -3)}y` : w.endsWith('oes') || w.endsWith('ches') || w.endsWith('shes') ? w.slice(0, -2) : w.endsWith('s') && !w.endsWith('ss') ? w.slice(0, -1) : w;

// The name's core words: "2 large eggs, beaten" -> ["egg"], "all-purpose
// flour" -> ["all-purpose", "flour"].
const coreWords = (name: string): string[] =>
  words(name.split(/[,(]/)[0]).filter((w) => !DESCRIPTORS.has(w)).map(singular);

const containsPhrase = (haystack: string[], phrase: string[]) =>
  phrase.length > 0 && haystack.some((_, i) => phrase.every((w, j) => haystack[i + j] === w));

// Which step first mentions each ingredient: by its full core name, else by
// its last word ("butter" for "unsalted butter") if no other ingredient
// shares that word. -1 if it can't be found.
const assignIngredients = (ingredients: Ingredient[], steps: string[]): number[] => {
  const stepWords = steps.map((s) => words(s).map(singular));
  const cores = ingredients.map((ing) => coreWords(ing.name));
  const heads = cores.map((c) => c[c.length - 1]);
  return cores.map((core, i) => {
    const full = stepWords.findIndex((sw) => containsPhrase(sw, core));
    if (full !== -1) return full;
    const head = heads[i];
    if (!head || heads.filter((h) => h === head).length > 1) return -1;
    return stepWords.findIndex((sw) => sw.includes(head));
  });
};

const TEMP = /\d+\s*°\s*[FC](?:\s*\(\d+\s*°\s*[FC]\))?/i;
const DURATION = /\d+(?:\s*(?:-|–|to)\s*\d+)?\s*(?:min(?:ute)?s?|hours?|hrs?)\b/i;

// "In a saucepan, melt the butter over low heat" -> "melt";
// "Bake at 350°F for 30-35 minutes" -> "bake 350°F 30-35 minutes".
export const actionLabel = (step: string): string => {
  const w = words(step);
  const at = w.findIndex((word) => VERBS.has(word));
  let label = at === -1 ? (w[0] || '') : w[at];
  if (at !== -1 && PARTICLES.has(w[at + 1])) label += ` ${w[at + 1]}`;
  const extras = [step.match(TEMP)?.[0], step.match(DURATION)?.[0]].filter(Boolean);
  return [label, ...extras].join(' ').trim();
};

export const deriveCard = (ingredients: Ingredient[], instructions: string): RecipeCardData | null => {
  const steps = splitInstructions(instructions);
  if (!steps.length || !ingredients.length) return null;

  const stepOf = assignIngredients(ingredients, steps);
  if (stepOf.some((s) => s === -1)) return null;

  const firstCooking = Math.min(...stepOf);
  const setup = steps.slice(0, firstCooking);
  if (setup.some((s) => s.length > 90)) return null;

  const cardSteps: RecipeCardData['steps'] = [];
  steps.slice(firstCooking).forEach((text, offset) => {
    const stepIndex = firstCooking + offset;
    cardSteps.push({
      action: actionLabel(text),
      ingredients: stepOf.map((s, i) => (s === stepIndex ? i : -1)).filter((i) => i !== -1),
      steps: cardSteps.length ? [cardSteps.length - 1] : [],
    });
  });

  return { setup, steps: cardSteps };
};
